import { Checkbox, FilterChip, SupplierItemAutocomplete } from '@retail/components';
import { AutocompleteInput, SelectInputField } from '@shared/components';
import { LevelSelectSchema, SelectableLevel } from './levelSelectSchema';
import { useController, useFormContext } from 'react-hook-form';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { SelectChangeEvent, Stack } from '@mui/material';
import {
  DiscountGroup,
  MgSupplierItemSearchResult,
  ProductGroupCount,
} from '@retail/products/types';

interface Props {
  levels: SelectableLevel[];
  productGroups: ProductGroupCount[];
  discountGroups?: DiscountGroup[];
  mgSupplierItems?: MgSupplierItemSearchResult[];
  isLoadingFetchItems: boolean;
  isLoading?: boolean;
  excludeWithExcistingConditions?: {
    isOpen: boolean;
    onToggle: () => void;
  };
  handleLevelSelectChange: (value: SelectChangeEvent<unknown>) => void;
  externalItemSource: 'FINFO' | 'NOBB';
}

export function LevelSelectForm({
  levels,
  productGroups,
  mgSupplierItems,
  discountGroups,
  isLoadingFetchItems,
  isLoading,
  excludeWithExcistingConditions,
  handleLevelSelectChange,
  externalItemSource,
}: Props) {
  const t = useCalculusTFunction();
  const { control, watch } = useFormContext<LevelSelectSchema>();

  const levelController = useController({ name: 'level', control });
  const overGroupController = useController({ name: 'overGroup', control });
  const mainGroupController = useController({ name: 'mainGroup', control });
  const productGroupController = useController({ name: 'productGroup', control });
  const discountGroupsController = useController({ name: 'discountGroups', control });
  const itemController = useController({ name: 'item', control });
  const itemSearchController = useController({ name: 'itemSearch', control });

  const levelValue = watch('level');
  const overGroupValue = watch('overGroup');
  const mainGroupValue = watch('mainGroup');

  const levelNames: Record<SelectableLevel, string> = {
    assortment: t('calculus.level.assortment'),
    supplier: t('calculus.level.supplier'),
    productGroup: t('calculus.level.productGroup'),
    discountGroup: t('calculus.level.discountGroup'),
    item: t('calculus.level.item.label'),
  };

  const levelMenuItems = levels
    .filter((level) => level !== 'discountGroup' || discountGroups)
    .map((level) => ({ value: level, name: levelNames[level] }));

  return (
    <Stack gap={1}>
      <SelectInputField
        key={levelController.field.value}
        {...levelController.field}
        disabled={isLoading}
        value={levelController.field.value ?? ''}
        error={levelController.fieldState.error?.message}
        size="small"
        width={400}
        label={t('calculus.level.level')}
        placeholder={t('calculus.actions.chooseLevel')}
        menuItems={levelMenuItems}
        onChange={handleLevelSelectChange}
        required
      />

      {levelValue === 'productGroup' && (
        <Stack flexDirection="row" gap={1}>
          <AutocompleteInput
            {...overGroupController.field}
            error={overGroupController.fieldState.error?.message}
            required
            width={260}
            size="small"
            label={t('calculus.level.overGroup')}
            placeholder={t('calculus.actions.chooseOverGroup')}
            showPopupIcon
            options={productGroups}
            getOptionLabel={(option) => option.getLabel()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => overGroupController.field.onChange(value)}
          />
          {!!overGroupValue && (
            <AutocompleteInput
              {...mainGroupController.field}
              width={260}
              size="small"
              label={t('calculus.level.mainGroup')}
              placeholder={t('calculus.actions.chooseMainGroup')}
              showPopupIcon
              options={
                productGroups.find(
                  (overGroup) => overGroup.groupNumber === overGroupValue.groupNumber
                )?.subGroups ?? []
              }
              getOptionLabel={(option) => option.getLabel()}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => mainGroupController.field.onChange(value)}
            />
          )}
          {!!mainGroupValue && (
            <AutocompleteInput
              {...productGroupController.field}
              width={260}
              size="small"
              label={t('calculus.level.productGroup')}
              placeholder={t('calculus.actions.chooseProductGroup')}
              showPopupIcon
              options={
                productGroups
                  .flatMap((overGroup) => overGroup.subGroups)
                  .find((mainGroup) => mainGroup?.groupNumber === mainGroupValue.groupNumber)
                  ?.subGroups ?? []
              }
              getOptionLabel={(option) => option.getLabel()}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => productGroupController.field.onChange(value)}
            />
          )}
        </Stack>
      )}

      {levelValue === 'discountGroup' && (
        <AutocompleteInput
          {...discountGroupsController.field}
          error={discountGroupsController.fieldState.error?.message}
          width={400}
          size="small"
          label={t('calculus.level.discountGroups')}
          placeholder={t('calculus.actions.chooseDiscountGroups')}
          showPopupIcon
          multiple
          options={discountGroups ?? []}
          getOptionLabel={(option) => `${option.code} ${option.name}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => discountGroupsController.field.onChange(value)}
          renderTags={(tags) =>
            tags.map((tag) => (
              <FilterChip
                key={tag.id}
                name={`${tag.code} ${tag.name}`}
                onDelete={() => {
                  discountGroupsController.field.onChange(
                    discountGroupsController.field.value.filter(
                      (dg: DiscountGroup) => dg.id !== tag.id
                    )
                  );
                }}
              />
            ))
          }
        />
      )}

      {(levelValue === 'productGroup' || levelValue === 'discountGroup') &&
        excludeWithExcistingConditions && (
          <Stack px={2}>
            <Checkbox
              label={t('calculus.excludeExcistingConditionLevel')}
              checked={excludeWithExcistingConditions.isOpen}
              onChange={excludeWithExcistingConditions.onToggle}
            />
          </Stack>
        )}

      {levelValue === 'item' && (
        <SupplierItemAutocomplete
          error={itemController.fieldState.error?.message}
          label={t('calculus.level.item.label')}
          placeholder={t('calculus.level.item.placeholder')}
          size="small"
          sx={{ width: 400 }}
          searchResults={mgSupplierItems || []}
          isLoading={isLoadingFetchItems}
          selectOption={(e) => itemController.field.onChange(e)}
          inputField={itemSearchController.field}
          externalItemSource={externalItemSource}
          required
        />
      )}
    </Stack>
  );
}
